import type { CancellableWidgetRequestArgs } from '../types';
import type { GetSessionStatusPayload } from './get-session-status';
import { getSessionStatus } from './get-session-status';

export const POLL_DELAY = 10000;
export const MAX_POLL_ATTEMPTS = 1000;

type PollSessionStatusArgs = CancellableWidgetRequestArgs & {
  sessionId: string;
  validateStatus: (status: GetSessionStatusPayload) => boolean;
};

export async function pollSessionStatus({
  sessionId,
  validateStatus,
  widgetToken,
  abortSignal
}: PollSessionStatusArgs) {
  let currentAttempt = 1;

  while (!abortSignal?.aborted && currentAttempt <= MAX_POLL_ATTEMPTS) {
    const status = await getSessionStatus({
      sessionId,
      widgetToken,
      abortSignal
    });

    if (validateStatus(status)) {
      return status;
    }

    await delayWithAbort({ timeMs: POLL_DELAY, abortSignal });
  }

  throw new Error('Poll was stopped or aborted');
}

async function delayWithAbort({
  abortSignal,
  timeMs = 1000
}: {
  timeMs?: number;
  abortSignal?: AbortSignal;
}) {
  return new Promise((resolve, reject) => {
    const delayTimeout = setTimeout(resolve, timeMs);

    abortSignal?.addEventListener(
      'abort',
      () => {
        clearTimeout(delayTimeout);
        reject(new Error('Poll was aborted'));
      },
      { once: true }
    );
  });
}
